import { message } from 'antd';
import { CancelToken } from 'axios';
import { NewUser } from 'models';
import { Bank } from '../models/bank';
import { IRole, Organization, OrganizationReferral, OrganizationType } from '../models/organization';
import { Api } from './api';
import { All, BaseGetAllParams } from './defaults';

export interface OrganizationCreateDto {
  name: string;
  abn: string;
  email: string;
  organizationType: OrganizationType;
  isGstRegistered: boolean;
  idCountry: string;
  idState: string;
  timezone: string;
}

export interface OrganizationUpdateDto {
  name: string;
  email: string;
  organizationType: OrganizationType;
  phoneNumber?: string;
  addressLine1?: string;
  addressLine2?: string;
  abn: string;
  paymentTerms?: number;
  invoicePrefix?: string;
  bankName?: Bank;
  bankNameOther?: string;
  bankBsb?: string;
  bankAccountNumber?: string;
}

export interface OrganizationInviteDto {
  idRole: string;
  email: string;
  initials: string;
  color: string;
  firstName?: string;
  lastName?: string;
}

export interface StaffUpdateDto {
  initials: string;
  color: string;
  idRole: string;
}

export async function GetAll(filters?: BaseGetAllParams & { text?: string }, options?: { cancelToken?: CancelToken }) {
  const result = await Api.get<any, All<Organization>>(`/organizations`, filters, options?.cancelToken);
  return result;
}

export async function GetAllUsers(idOrganization: string, filters?: BaseGetAllParams & { text?: string }, options?: { cancelToken?: CancelToken }) {
  const result = await Api.get<any, All<NewUser>>(`/organizations/${idOrganization}/users`, filters, options?.cancelToken);
  return result;
}

export async function GetReferrals(idOrganization: string, filters?: BaseGetAllParams, options?: { cancelToken?: CancelToken }) {
  const result = await Api.get<any, All<OrganizationReferral>>(`/organizations/${idOrganization}/referrals`, filters, options?.cancelToken);
  return result;
}

export async function GetRoles(idOrganization: string, options?: { cancelToken?: CancelToken }) {
  const result = await Api.get<any, All<IRole>>(`/organizations/${idOrganization}/roles`, undefined, options?.cancelToken);
  return result;
}

export async function Create(data: OrganizationCreateDto) {
  const result = await Api.post<OrganizationCreateDto, Organization>('/organizations', data);
  return result;
}

export async function Update(organizationId: string, data: OrganizationUpdateDto) {
  const result = await Api.put<OrganizationUpdateDto, Organization>(`/organizations/${organizationId}`, data);
  message.success('Business settings updated successfully');
  return result;
}

export async function InviteStaff(organizationId: string, data: OrganizationInviteDto) {
  const result = await Api.post<OrganizationInviteDto, any>(`/organizations/${organizationId}/invite`, data);
  message.success('Staff invited successfully');
  return result;
}

export async function UpdateStaff(organizationId: string, userId: string, data: StaffUpdateDto) {
  const result = await Api.put<StaffUpdateDto, any>(`/organizations/${organizationId}/users/${userId}`, data);
  message.success('Staff updated successfully');
  return result;
}

export async function DisableStaff(organizationId: string, userId: string) {
  const result = await Api.post<OrganizationInviteDto, any>(`/organizations/${organizationId}/users/${userId}/disable`);
  message.success('Staff disabled successfully');
  return result;
}

export async function EnableStaff(organizationId: string, userId: string) {
  const result = await Api.post<OrganizationInviteDto, any>(`/organizations/${organizationId}/users/${userId}/enable`);
  message.success('Staff enabled successfully');
  return result;
}

export async function Export() {
  const result = await Api.post<OrganizationInviteDto, any>(`/organizations/export`);
  message.success('Organizations exported successfully');
  return result;
}
